import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { registerConfirmSteps, registerScreenActions, registerScreenPanels } from '../../studio-nav/screen-panels.service';
import { FormsModule } from '@angular/forms';
import { ClientService } from '../../services/client.service';
import { GlobalVariablesService } from '../../services/global-variables.service';
import { MembersService, ProjectMember } from '../../services/members.service';
import { RoleService } from '../../services/role.service';
import { ConfirmWaitsNgService } from '../../services/mcp/actions/confirm-waits-ng.service';
import type { Prefilled, Prepared, SaveOutcome } from '../../services/mcp/actions/confirm-lane';

type Level = 'owner' | 'editor' | 'viewer';

const ADD_COMMIT = 'team-add-member';

/**
 * Project Access page (repurposed user-management). Shows who can edit THIS project (owner/editor/
 * viewer) and lets the owner add people BY SECRET CODE / remove them. Builder access only — no
 * end-user or role UI here. See olympus-functions docs/plans/builder-access-control.md.
 */
@Component({
    selector: 'app-user-mgmt',
    imports: [CommonModule, FormsModule],
    templateUrl: './user-mgmt.component.html',
    host: { class: 'block h-full' },
    styles: [`
    :host { display: block; height: 100%; }
    .custom-scroll::-webkit-scrollbar { width: 5px; height: 5px; }
    .custom-scroll::-webkit-scrollbar-thumb { border-radius: 3px; }
    .theme-light .custom-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.08); }
    .theme-dark .custom-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); }
    @keyframes umModalPop {
      from { opacity: 0; transform: translateY(10px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }
    `]
})
export class UserMgmtComponent implements OnInit {
  get darkMode(): boolean { return this.globalVariablesService.pageThemeDark; }
  get projectName(): string { return this.clientService.selectedClient?.name || 'this project'; }

  members: ProjectMember[] = [];
  loading = false;
  myAccountId = '';

  // add-by-code flow
  showAdd = false;
  private panels = registerScreenPanels({
    screen: 'team',
    loading: () => this.loading,
    here: () => (this.showAdd ? { panel: 'add-member' } : {}),
    // Was (2026-10-30): holds: () => ({ members: this.members, appRoles: this.appRoles, code: this.code, level: this.chosenLevel }),
    // Each member names its fields, so a secret on a server response can't ride along.
    holds: () => ({
      // read_screen says not read for what a screen doesn't hold; an empty list here would read as an empty team.
      ...(this.membersReadFailed ? {} : { members: this.members.map((m) => ({ accountId: m.accountId, name: m.name, email: m.email, access_level: m.access_level, zavoTeam: m.zavoTeam, app_role_id: m.app_role_id })) }),
      appRoles: this.appRoles, code: this.code, level: this.chosenLevel,
    }),
    // Only an owner has the button, and the access level arrives after the screen does.
    select: () => {
      if (!this.amOwner) return false;
      if (!this.showAdd) this.openAdd();
      return true;
    },
    unsaved: () => (this.showAdd && this.code.trim() ? { count: 1, name: 'The add-member form' } : null),
  });
  private offers = registerScreenActions({
    'reload-list': async () => {
      await this.refresh();
      // A failed read leaves the list empty, and a project always has its owner.
      if (!this.members.length) throw new Error('The team could not be read.');
      return { done: 'Reloaded the team.', result: { shown: this.members.length } };
    },
  });
  private membersReadFailed = false;
  private waits = inject(ConfirmWaitsNgService);
  private confirmSteps = registerConfirmSteps({
    [ADD_COMMIT]: { press: (values) => this.prefillAdd(values) },
    'change-team-level': { overlay: (values) => this.prepareLevel(values) },
    'change-app-role': { overlay: (values) => this.prepareAppRole(values) },
    'team-remove-member': { overlay: (values) => this.prepareRemove(values) },
  });
  code = '';
  resolving = false;
  resolved: { accountId: string; name: string | null; email: string | null } | null = null;
  chosenLevel: Level = 'editor';
  addError = '';
  rowError = '';
  rowErrorAccountId = '';

  // Which row's level menu is open (accountId), or null. One at a time.
  openMenuFor: string | null = null;

  /** The project's app roles, for the picker. Reserved 'admin' is included by listRoles. */
  appRoles: { id: string; name: string }[] = [];
  /** accountId → the role being saved right now, so one row can show a spinner. */
  savingRole = '';
  roleError = '';

  /** The three levels, with the one-line explanation shown in the menu. */
  readonly LEVELS: { id: Level; label: string; blurb: string }[] = [
    { id: 'owner',  label: 'Owner',  blurb: 'Add, remove and change access' },
    { id: 'editor', label: 'Editor', blurb: 'Build pages, data and functions' },
    { id: 'viewer', label: 'Viewer', blurb: 'Read-only access' },
  ];

  constructor(
    private membersService: MembersService,
    private globalVariablesService: GlobalVariablesService,
    private clientService: ClientService,
    private roleService: RoleService,
  ) {}

  async ngOnInit(): Promise<void> {
    const me = await this.membersService.getMyAccount();
    this.myAccountId = me?.accountId || '';
    await this.refresh();
  }

  async refresh(): Promise<void> {
    this.loading = true;
    const read = await this.membersService.listMembers();
    this.membersReadFailed = read === null;
    this.members = read ?? [];
    // RoleService.query's 2nd param is NOT "include reserved" in this codebase — it's whether the
    // call also pushes into RoleService's shared $values stream. Matching admin-users.component.ts's
    // own roleDefs call (query(1000, false)) so this page doesn't clobber that shared state.
    this.appRoles = await this.roleService.query(1000, false).catch(() => []);
    this.loading = false;
  }

  /** An owner sets anyone's; everyone else only their own (the server enforces the same rule). */
  canSetAppRole(m: { accountId: string }): boolean {
    return this.amOwner || m.accountId === this.myAccountId;
  }

  async changeAppRole(m: { accountId: string; app_role_id?: string }, roleId: string): Promise<SaveOutcome> {
    const saved = { outcome: 'saved' as const, saved: { memberId: m.accountId, appRoleId: roleId } };
    if (!roleId || roleId === (m.app_role_id || 'admin')) return saved;
    this.savingRole = m.accountId;
    this.roleError = '';
    const res = await this.membersService.setAppRole(m.accountId === this.myAccountId ? '' : m.accountId, roleId);
    let outcome: SaveOutcome = saved;
    if (res.ok) {
      m.app_role_id = roleId;
    } else {
      this.roleError = res.error || 'Could not change the role.';
      // refresh(), not a local reset: it replaces this.members with fresh objects, which rebuilds
      // the row and snaps the rejected <select> back to the stored role. Angular will not re-write
      // [selected] on its own here — the bound expression (m.app_role_id) never changed, only the
      // live DOM value the browser applied when the option was clicked.
      await this.refresh();
      outcome = { outcome: 'failed', reason: this.roleError };
    }
    this.savingRole = '';
    return outcome;
  }

  get amOwner(): boolean {
    return this.members.find(m => m.accountId === this.myAccountId)?.access_level === 'owner';
  }
  isMe(m: ProjectMember): boolean { return m.accountId === this.myAccountId; }
  ownerCount(): number { return this.members.filter(m => m.access_level === 'owner').length; }
  canRemove(m: ProjectMember): boolean {
    return this.amOwner && !this.isMe(m) && !(m.access_level === 'owner' && this.ownerCount() <= 1);
  }
  /** Owners may change any level, except the last owner's — theirs or anyone's. Mirrors the CF's
   *  rule exactly, so the UI never offers a change the backend will reject. */
  canChangeLevel(m: ProjectMember): boolean {
    return this.amOwner && !(m.access_level === 'owner' && this.ownerCount() <= 1);
  }

  toggleMenu(m: ProjectMember, event?: Event): void {
    event?.stopPropagation();          // keep the document listener from closing it immediately
    this.rowError = '';
    this.openMenuFor = this.openMenuFor === m.accountId ? null : m.accountId;
  }

  /** Close on any outside click or Escape — a menu you can only dismiss by choosing is a trap. */
  @HostListener('document:click')
  @HostListener('document:keydown.escape')
  closeMenu(): void { this.openMenuFor = null; }

  /**
   * Why `level` cannot be applied to `m`, or '' if it can. Shown in place of the level's blurb on a
   * disabled option, so the last-owner rule explains itself instead of the option silently vanishing.
   */
  levelDisabledReason(m: ProjectMember, level: Level): string {
    if (m.access_level === 'owner' && level !== 'owner' && this.ownerCount() <= 1) {
      return 'A project must always have an owner';
    }
    return '';
  }

  /** `alreadyConfirmed`: the builder has just clicked a card that said the same thing as these prompts. */
  async changeLevel(m: ProjectMember, next: Level, alreadyConfirmed = false): Promise<SaveOutcome> {
    this.openMenuFor = null;
    this.rowError = '';
    this.rowErrorAccountId = '';
    const saved = { outcome: 'saved' as const, saved: { memberId: m.accountId, level: next } };
    const refused = this.levelDisabledReason(m, next);
    if (refused) return { outcome: 'failed', reason: refused };
    if (next === m.access_level) return saved;

    // Confirm only on escalation and on stepping down. Demoting someone else is reversible by me, so
    // it stays silent — a dialog on every change just teaches people to click through them.
    if (!alreadyConfirmed && next === 'owner' &&
        !confirm(`Make ${m.name || m.email || m.accountId} an owner? Owners can add and remove members and change anyone's access.`)) {
      await this.refresh(); return { outcome: 'discarded' };
    }
    if (!alreadyConfirmed && this.isMe(m) && m.access_level === 'owner' && next !== 'owner' &&
        !confirm(`Step down to ${next}? You'll lose owner access to ${this.projectName} and can't restore it yourself.`)) {
      await this.refresh(); return { outcome: 'discarded' };
    }

    const r = await this.membersService.setMemberLevel(m.accountId, next);
    if (!r.ok) { this.rowError = r.error || 'Failed to change access level.'; this.rowErrorAccountId = m.accountId; }
    // refresh() on BOTH paths: it replaces this.members with fresh objects, which rebuilds the rows
    // and snaps a rejected <select> back to the real value. Mutating in place would not — Angular
    // only writes to the DOM when the bound value changes, and on a failed change it has not.
    await this.refresh();
    return r.ok ? saved : { outcome: 'failed', reason: this.rowError };
  }

  initials(m: ProjectMember): string {
    return (m.name || m.email || '?').trim().slice(0, 2).toUpperCase();
  }

  openAdd(): void { this.showAdd = true; this.code = ''; this.resolved = null; this.addError = ''; this.chosenLevel = 'editor'; }
  closeAdd(): void {
    this.showAdd = false;
    this.waits.report(ADD_COMMIT, { outcome: 'discarded' });
  }

  async resolve(): Promise<void> {
    this.addError = '';
    this.resolved = null;
    if (!this.code.trim()) return;
    this.resolving = true;
    this.resolved = await this.membersService.resolveCode(this.code.trim());
    this.resolving = false;
    if (!this.resolved) this.addError = 'No account found for that code.';
  }

  async add(): Promise<void> {
    if (!this.resolved) return;
    this.addError = '';
    this.waits.saving(ADD_COMMIT);
    const added = { name: this.resolved.name, email: this.resolved.email, level: this.chosenLevel };
    const r = await this.membersService.addMember(this.code.trim(), this.chosenLevel);
    if (r.ok) {
      this.waits.report(ADD_COMMIT, { outcome: 'saved', saved: added });
      this.closeAdd();
      await this.refresh();
    } else {
      this.addError = r.error || 'Failed to add member.';
      this.waits.report(ADD_COMMIT, { outcome: 'failed', reason: this.addError });
    }
  }

  /** `alreadyConfirmed`: the builder has just clicked a card that said the same thing as this prompt. */
  async remove(m: ProjectMember, alreadyConfirmed = false): Promise<SaveOutcome> {
    if (!this.canRemove(m)) return { outcome: 'failed', reason: `${this.whoIs(m)} can't be removed.` };
    this.rowError = ''; this.rowErrorAccountId = '';
    if (!alreadyConfirmed && !confirm(`Remove ${m.email || m.name || m.accountId} from ${this.projectName}? They will need their code to be re-added.`)) return { outcome: 'discarded' };
    if (await this.membersService.removeMember(m.accountId)) {
      await this.refresh();
      return { outcome: 'saved', saved: { memberId: m.accountId, removed: m.email || m.name || m.accountId } };
    }
    this.rowError = 'Failed to remove member.'; this.rowErrorAccountId = m.accountId;
    return { outcome: 'failed', reason: this.rowError };
  }

  private whoIs(m: ProjectMember): string { return m.name || m.email || m.accountId; }

  private isLevel(level: unknown): level is Level { return this.LEVELS.some((l) => l.id === level); }

  /** Fills the add form the way the builder would: the code is checked first, because the form's level and button only appear for an account that exists. */
  private async prefillAdd(values: Record<string, unknown>): Promise<Prefilled> {
    if (!this.amOwner) return { refused: 'Only an owner can add someone to the team. Nothing was changed.' };
    const code = String(values['code'] ?? '').trim();
    if (!code) return { refused: 'Adding someone needs their builder code. Nothing was changed.' };
    const level = values['level'] ?? 'editor';
    if (!this.isLevel(level)) return { refused: 'The level has to be owner, editor or viewer. Nothing was changed.' };
    if (this.panels.unsaved?.()) return { refused: 'The builder has the add form open with a code in it. Nothing was changed. Ask them to finish or close it, then try again.' };

    this.openAdd();
    this.code = code;
    await this.resolve();
    if (!this.showAdd) return { refused: 'The builder closed the add form. Nothing was changed.' };
    if (!this.resolved) {
      this.closeAdd();
      return { refused: 'No account found for that code. Nothing was changed.' };
    }
    this.chosenLevel = level;
    return {};
  }

  private memberAt(values: Record<string, unknown>): ProjectMember | undefined {
    return this.members.find((m) => m.accountId === values['memberId']);
  }

  private static readonly NO_MEMBER: Prepared = { refused: 'No team member has that id; read_screen lists them under members. Nothing was changed.' };

  /** `refresh` replaces the list's objects, so a retry needs the person as they are now, not as the card first saw them. */
  private onMember(accountId: string, act: (m: ProjectMember) => Promise<SaveOutcome>): () => Promise<SaveOutcome> {
    return async () => {
      const m = this.members.find((x) => x.accountId === accountId);
      return m ? act(m) : { outcome: 'failed', reason: 'That person is no longer on the team.' };
    };
  }

  private async prepareLevel(values: Record<string, unknown>): Promise<Prepared> {
    const m = this.memberAt(values);
    if (!m) return UserMgmtComponent.NO_MEMBER;
    const next = values['level'];
    if (!this.isLevel(next)) return { refused: 'The level has to be owner, editor or viewer. Nothing was changed.' };
    if (!this.amOwner) return { refused: 'Only an owner can change a level. Nothing was changed.' };
    if (next === m.access_level) return { answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', memberId: m.accountId, level: next } } };
    const why = this.levelDisabledReason(m, next);
    if (why) return { refused: `${why}. Nothing was changed.` };

    let says = `Change ${this.whoIs(m)} from ${m.access_level} to ${next}.`;
    if (next === 'owner') says = `Make ${this.whoIs(m)} an owner. Owners can add and remove members and change anyone's access.`;
    else if (this.isMe(m) && m.access_level === 'owner') says = `Step down to ${next}. You'll lose owner access to ${this.projectName} and can't restore it yourself.`;
    return { says, confirmLabel: 'Change level', run: this.onMember(m.accountId, (current) => this.changeLevel(current, next, true)) };
  }

  private async prepareAppRole(values: Record<string, unknown>): Promise<Prepared> {
    const m = this.memberAt(values);
    if (!m) return UserMgmtComponent.NO_MEMBER;
    if (!this.canSetAppRole(m)) return { refused: 'Only an owner can change someone else\'s app role. Nothing was changed.' };
    const role = this.appRoles.find((r) => r.id === values['appRoleId']);
    if (!role) return { refused: 'This project has no app role with that id; read_screen lists them under appRoles. Nothing was changed.' };
    if (role.id === (m.app_role_id || 'admin')) return { answer: { outcome: 'done', changed: [], result: { outcome: 'unchanged', memberId: m.accountId, appRoleId: role.id } } };
    return {
      says: `Give ${this.whoIs(m)} the app role ${role.name}.`,
      confirmLabel: 'Change role',
      run: this.onMember(m.accountId, (current) => this.changeAppRole(current, role.id)),
    };
  }

  private async prepareRemove(values: Record<string, unknown>): Promise<Prepared> {
    const m = this.memberAt(values);
    if (!m) return UserMgmtComponent.NO_MEMBER;
    if (!this.canRemove(m)) {
      return { refused: `${this.whoIs(m)} can't be removed: only an owner can remove people, not themselves, and the last owner stays. Nothing was changed.` };
    }
    return {
      says: `Remove ${m.email || m.name || m.accountId} from ${this.projectName}. They will need their code to be re-added.`,
      confirmLabel: 'Remove',
      run: this.onMember(m.accountId, (current) => this.remove(current, true)),
    };
  }
}
